import { DotSeparatedList, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { parseISO } from "date-fns";

import { PayRowLabel } from "../../../../Pay/PayRowLabel";
import { formatDayOfMonthWithDayName } from "../../../../utils/formatDayOfMonthWithDayName";
import { formatHoursDurationCompact } from "../../../../utils/formatHoursDurationCompact";
import { formatTime } from "../../../../utils/formatTime";
import { type ExtraWorkedTimeRequestType } from "../../../api/usePaginatedWorkerShifts";
import { getPayTextColor } from "../../../components/getPayTextColor";
import { ShiftStatusPill } from "../../../components/ShiftStatusPill";
import { getShiftHistoryListItemVariant } from "./getShiftHistoryListItemVariant";

interface ShiftHistoryListItemProps {
  isVerified: boolean;
  shiftStartDate: string | undefined;
  clockInTime: string | undefined;
  clockOutTime: string | undefined;
  workplaceName: string | undefined;
  workplaceTimezone: string | undefined;
  hourlyRate: number;
  totalShiftTimeInHours: number;
  request: ExtraWorkedTimeRequestType["attributes"] | undefined;
  onClick?: () => void;
}

export function ShiftHistoryListItem(props: ShiftHistoryListItemProps) {
  const {
    isVerified,
    shiftStartDate,
    clockInTime,
    clockOutTime,
    workplaceName,
    workplaceTimezone: workplaceTimezoneProperty,
    hourlyRate,
    totalShiftTimeInHours,
    request,
    onClick,
  } = props;

  const variant = getShiftHistoryListItemVariant(isVerified, request);
  const isFullShiftPayment = request?.isFullShiftPayment ?? false;
  const workplaceTimezone = workplaceTimezoneProperty ?? DEFAULT_TIMEZONE;
  const totalPayInDollars = totalShiftTimeInHours * hourlyRate;
  const requestedAmountInDollars = isDefined(request?.timeRequestedInMinutes)
    ? hourlyRate * (request.timeRequestedInMinutes / 60)
    : undefined;

  const startDate = isDefined(shiftStartDate) ? parseISO(shiftStartDate) : undefined;

  const isClockInMissing = !isDefined(clockInTime);
  const isClockOutMissing = !isDefined(clockOutTime);

  const displayStartTime = isClockInMissing
    ? "Missing clock in"
    : formatTime(parseISO(clockInTime), workplaceTimezone).toLowerCase();

  const displayEndTime = isClockOutMissing
    ? "Missing clock out"
    : formatTime(parseISO(clockOutTime), workplaceTimezone).toLowerCase();

  const durationLabel = formatHoursDurationCompact(totalShiftTimeInHours, {
    omitZeroMinutes: true,
  });

  const formattedDate = isDefined(startDate)
    ? formatDayOfMonthWithDayName(startDate, { timezone: workplaceTimezone })
    : "";

  if (!isDefined(shiftStartDate) || !isDefined(workplaceName)) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingY: theme.spacing(5),
        borderBottom: `1px solid ${theme.palette.border?.subtle}`,
        backgroundColor: theme.palette.background.tertiary,
        cursor: isDefined(onClick) ? "pointer" : undefined,
      })}
      onClick={onClick}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          <Text semibold variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
            {formattedDate}
          </Text>
          <Text semibold variant="body1">
            {workplaceName}
          </Text>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            <DotSeparatedList>
              <Box component="span">
                <Box
                  component="span"
                  sx={(theme) => ({
                    color: isClockInMissing ? theme.palette.text.tertiary : "inherit",
                  })}
                >
                  {`${displayStartTime} - `}
                </Box>
                <Box
                  component="span"
                  sx={(theme) => ({
                    color: isClockOutMissing ? theme.palette.text.tertiary : "inherit",
                  })}
                >
                  {displayEndTime}
                </Box>
              </Box>
              {isVerified ? <Box component="span">{durationLabel}</Box> : undefined}
            </DotSeparatedList>
          </Text>
        </Stack>

        <Stack alignItems="flex-end" justifyContent="center" spacing={2} sx={{ flexShrink: 0 }}>
          {variant !== "unverified" && (
            <PayRowLabel
              primaryAmount={formatDollarsAsUsd(
                isFullShiftPayment &&
                  isDefined(requestedAmountInDollars) &&
                  requestedAmountInDollars > 0
                  ? requestedAmountInDollars
                  : totalPayInDollars
              )}
              adjustmentAmount={
                isDefined(requestedAmountInDollars) &&
                requestedAmountInDollars > 0 &&
                !isFullShiftPayment
                  ? `(+${formatDollarsAsUsd(requestedAmountInDollars)})`
                  : undefined
              }
              primaryAmountSx={(theme) => ({
                typography: "body1",
                fontWeight: "bold",
                color: isFullShiftPayment
                  ? getPayTextColor(theme, variant)
                  : theme.palette.text.primary,
              })}
              adjustmentAmountSx={
                isFullShiftPayment
                  ? undefined
                  : (theme) => ({
                      fontWeight: "bold",
                      color: getPayTextColor(theme, variant),
                    })
              }
            />
          )}
          {variant !== "verified" && <ShiftStatusPill status={variant} />}
        </Stack>
      </Stack>
    </Box>
  );
}
